import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, Plus, Send, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { ListPagination } from '@/components/efs/ListPagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PageHeader } from '@/components/efs/PageHeader';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';

import { DispatchStatusBadge } from '../DispatchStatusBadge';
import { DispatchDateRangeFilter } from '../DispatchDateRangeFilter';
import { DispatchRequestPreviewSheet } from '../DispatchRequestPreviewSheet';
import { DispatchStopsSummary } from '../DispatchStopsSummary';
import { formatDispatchTime } from '../format';
import { SupplierRequestDialog } from './SupplierRequestDialog';
import { useSuppliersList } from '@/features/masters/suppliers/useSuppliers';
import {
  useCancelSupplierRequest,
  useSubmitSupplierRequest,
  useSupplierRequests,
} from '../useDispatch';
import type { DispatchRequestDto } from '../types';

const NS = 'operations';
const DEFAULT_PER_PAGE = 50;
const PAGE_SIZE_OPTIONS = [50, 100];

/**
 * Supplier list of own dispatch requests. The supplier can:
 *   - add a new draft via the dialog
 *   - edit an existing draft
 *   - submit a draft (status → submitted)
 *   - cancel any non-terminal row
 */
interface SupplierRequestsPageProps {
  embedded?: boolean;
}

export const SupplierRequestsPage = ({ embedded = false }: SupplierRequestsPageProps) => {
  const { t } = useTranslation(NS);
  const user = useCurrentUser();
  const [requestedDateFrom, setRequestedDateFrom] = useState('');
  const [requestedDateTo, setRequestedDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const list = useSupplierRequests({
    page,
    per_page: perPage,
    ...(requestedDateFrom ? { requested_date_from: requestedDateFrom } : {}),
    ...(requestedDateTo ? { requested_date_to: requestedDateTo } : {}),
  });
  const suppliersQuery = useSuppliersList({ active_only: false, per_page: 500 });
  const submit = useSubmitSupplierRequest();
  const cancel = useCancelSupplierRequest();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState<DispatchRequestDto | null>(null);
  const [previewRow, setPreviewRow] = useState<DispatchRequestDto | null>(null);

  const rows = list.data?.data ?? [];
  const meta = list.data?.meta;
  const total = meta?.total ?? rows.length;
  const supplierNameById = new Map(
    (suppliersQuery.data?.rows ?? []).map((supplier) => [
      supplier.company_id,
      supplier.display_name,
    ]),
  );

  const openCreate = () => {
    setEditRow(null);
    setDialogOpen(true);
  };
  const openEdit = (row: DispatchRequestDto) => {
    setEditRow(row);
    setDialogOpen(true);
  };

  const canCreate = hasPermission(user, 'dispatch.request.create');
  const canEdit = (r: DispatchRequestDto) =>
    ['draft', 'submitted', 'routed', 'accepted', 'rejected'].includes(r.status) &&
    (hasPermission(user, 'dispatch.request.update_draft') ||
      hasPermission(user, 'dispatch.request.edit'));
  const canSubmit = (r: DispatchRequestDto) =>
    r.status === 'draft' && hasPermission(user, 'dispatch.request.submit');
  const canCancel = (r: DispatchRequestDto) =>
    (r.status === 'draft' || r.status === 'submitted' || r.status === 'routed') &&
    hasPermission(user, 'dispatch.request.cancel');

  const onCancel = (row: DispatchRequestDto) => {
    const reason = window.prompt(
      t('efs.operations.dispatch.supplier.cancel_prompt', {
        defaultValue: '請輸入取消原因',
      }),
    );
    const trimmed = reason?.trim();
    if (!trimmed) return;
    cancel.mutate({ id: row.dispatch_request_id, body: { reason: trimmed } });
  };

  return (
    <div className="space-y-4">
      {!embedded ? (
        <PageHeader
          title={t('efs.operations.dispatch.supplier.title', {
            defaultValue: '配車・配送申請',
          })}
          subtitle={t('efs.operations.dispatch.supplier.subtitle', {
            defaultValue: '配車・配送依頼を作成・追跡',
          })}
          count={total}
          actions={
            canCreate ? (
              <Button onClick={openCreate}>
                <Plus className="mr-1 h-4 w-4" />
                {t('efs.operations.dispatch.actions.create', { defaultValue: '新增申請' })}
              </Button>
            ) : null
          }
        />
      ) : canCreate ? (
        <div className="flex justify-end">
          <Button onClick={openCreate}>
            <Plus className="mr-1 h-4 w-4" />
            {t('efs.operations.dispatch.actions.create', { defaultValue: '新增申請' })}
          </Button>
        </div>
      ) : null}

      <DispatchDateRangeFilter
        from={requestedDateFrom}
        to={requestedDateTo}
        onFromChange={(value) => {
          setPage(1);
          setRequestedDateFrom(value);
        }}
        onToChange={(value) => {
          setPage(1);
          setRequestedDateTo(value);
        }}
        onClear={() => {
          setPage(1);
          setRequestedDateFrom('');
          setRequestedDateTo('');
        }}
      />

      <div className="rounded-md border border-efs-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('efs.operations.dispatch.fields.supplier')}</TableHead>
              <TableHead>{t('efs.operations.dispatch.fields.trip_code')}</TableHead>
              <TableHead>{t('efs.operations.dispatch.fields.type')}</TableHead>
              <TableHead>{t('efs.operations.dispatch.fields.requested_date')}</TableHead>
              <TableHead>
                {t('efs.operations.dispatch.fields.requested_time', {
                  defaultValue: '希望時間',
                })}
              </TableHead>
              <TableHead>{t('efs.operations.dispatch.fields.location')}</TableHead>
              <TableHead>
                {t('efs.operations.dispatch.fields.status', { defaultValue: '狀態' })}
              </TableHead>
              <TableHead className="text-right">
                {t('efs.operations.dispatch.fields.actions', { defaultValue: '操作' })}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="py-6 text-center text-efs-text-muted">
                  {t('efs.common.messages.loading', { ns: 'common' })}
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="py-6 text-center text-efs-text-muted">
                  {t('efs.common.messages.empty', { ns: 'common' })}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.dispatch_request_id}>
                  <TableCell className="text-xs">
                    {supplierNameById.get(row.supplier_company_id) ??
                      `${row.supplier_company_id.slice(0, 8)}…`}
                  </TableCell>
                  <TableCell>
                    <div className="font-mono text-xs text-efs-text-muted">
                      {row.trip_code ?? row.temporary_code}
                    </div>
                    {!row.trip_code ? (
                      <div className="text-[11px] text-efs-text-muted">暫存代碼</div>
                    ) : null}
                  </TableCell>
                  <TableCell className="text-xs">
                    {t(`efs.operations.dispatch.types.${row.request_type}`, {
                      defaultValue: row.request_type,
                    })}
                  </TableCell>
                  <TableCell className="text-xs">{row.requested_date ?? '—'}</TableCell>
                  <TableCell className="text-xs">
                    {formatDispatchTime(row.requested_time)}
                  </TableCell>
                  <TableCell className="text-xs">
                    <DispatchStopsSummary row={row} />
                  </TableCell>
                  <TableCell>
                    <DispatchStatusBadge status={row.status} />
                    <p className="mt-1 text-xs text-efs-text-muted">
                      {t(`efs.operations.dispatch.status_descriptions.${row.status}`, {
                        defaultValue: row.status,
                      })}
                    </p>
                    {/* The processor's rejection reason is the supplier's cue
                        for how to retry (e.g. 満載なので別日に) — surface it
                        right on the row (field report 2026-07-31). */}
                    {row.rejection_reason ? (
                      <p className="mt-1 max-w-56 whitespace-pre-wrap text-xs font-medium text-destructive">
                        {t('efs.operations.dispatch.supplier.rejection_reason_label', {
                          defaultValue: '差し戻し理由',
                        })}
                        : {row.rejection_reason}
                      </p>
                    ) : null}
                    {row.return_suggested_availability?.length ? (
                      <p className="mt-1 max-w-56 text-xs font-medium text-destructive">
                        {t('efs.operations.dispatch.supplier.suggested_slots_label', {
                          defaultValue: '再配車可能日時',
                        })}
                        :{' '}
                        {row.return_suggested_availability
                          .map((slot) => `${slot.date} ${slot.start_time.slice(0, 5)}`)
                          .join(' / ')}
                      </p>
                    ) : null}
                  </TableCell>
                  <TableCell className="space-x-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setPreviewRow(row)}>
                      <Eye className="mr-1 h-3 w-3" aria-hidden="true" />
                      {t('efs.operations.dispatch.actions.preview', {
                        defaultValue: '檢視',
                      })}
                    </Button>
                    {canEdit(row) ? (
                      <Button size="sm" variant="outline" onClick={() => openEdit(row)}>
                        {t('efs.common.actions.open', {
                          ns: 'common',
                          defaultValue: '編輯',
                        })}
                      </Button>
                    ) : null}
                    {canSubmit(row) ? (
                      <Button
                        size="sm"
                        variant="default"
                        disabled={submit.isPending}
                        onClick={() => {
                          const value = window.prompt('CC Email（多筆請以逗號分隔，可留空）', '');
                          if (value === null) return;
                          const ccEmails = value
                            .split(',')
                            .map((email) => email.trim())
                            .filter(Boolean);
                          submit.mutate({ id: row.dispatch_request_id, ccEmails });
                        }}
                      >
                        <Send className="mr-1 h-3 w-3" />
                        {t('efs.operations.dispatch.actions.submit', {
                          defaultValue: '送出',
                        })}
                      </Button>
                    ) : null}
                    {canCancel(row) ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={cancel.isPending}
                        onClick={() => onCancel(row)}
                      >
                        <Trash2 className="mr-1 h-3 w-3" />
                        {t('efs.operations.dispatch.actions.cancel_request', {
                          defaultValue: '取消',
                        })}
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <ListPagination
        page={meta?.page ?? page}
        perPage={meta?.per_page ?? perPage}
        total={total}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={setPage}
        onPerPageChange={(nextPerPage) => {
          setPage(1);
          setPerPage(nextPerPage === 100 ? 100 : DEFAULT_PER_PAGE);
        }}
      />

      <SupplierRequestDialog
        key={editRow?.dispatch_request_id ?? 'new'}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        row={editRow}
      />
      <DispatchRequestPreviewSheet
        open={!!previewRow}
        onOpenChange={(open) => {
          if (!open) setPreviewRow(null);
        }}
        request={previewRow}
        supplierName={previewRow ? supplierNameById.get(previewRow.supplier_company_id) : undefined}
      />
    </div>
  );
};

export default SupplierRequestsPage;
